import {useState} from 'react';
import {Image} from '@shopify/hydrogen';
import {
  getRichLinkCardStyle,
  CardStyle,
  LinkCardShape,
} from 'shared/utils/CardStyleUtil';
import classNames from 'classnames';

import AmazonAffiliateLinkModal from '../AmazonAffiliateLinkModal/AmazonAffiliateLinkModal.client';
import {MonorailEvent, trackEvent} from 'utils/monorail.client';
import {usePageContext} from 'contexts/PageContext.client';
import {useErrorLogger} from '@shopify/react-bugsnag';

interface AmazonAffiliateLinkProps {
  title: string;
  description: string;
  style: CardStyle;
  url: string;
  amazonPrice?: string;
  media?: {url: string};
}

export const AmazonAffiliateLink = ({
  title,
  description,
  style,
  media,
  url,
  amazonPrice,
}: AmazonAffiliateLinkProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const cardStyle = getRichLinkCardStyle(style);
  const imageUrl = media?.url;
  const className = classNames(
    'flex',
    'w-full',
    'rounded',
    'bg-white',
    'shadow',
    'p-1',
    'gap-4',
  );

  const pageCtx = usePageContext();
  const logger = useErrorLogger();

  function trackAmazonCardClicked() {
    try {
      trackEvent({
        event: MonorailEvent.AmazonAffiliatePublicCardClicked,
        data: {
          pageId: pageCtx.id,
          linkUrl: url,
          linkTitle: title,
        },
      });
    } catch (error) {
      logger.notify(
        new Error(
          `event tracking error: ${MonorailEvent.AmazonAffiliatePublicCardClicked}: ${error}`,
        ),
      );
    }
  }

  function onCardClicked() {
    trackAmazonCardClicked();
    openModal();
  }

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      openModal();
    }
  }

  return (
    <>
      <div
        data-testid="amazon-affiliate-link"
        className={className}
        style={cardStyle}
        role="button"
        onKeyDown={handleKeyDown}
        onClick={onCardClicked}
        tabIndex={0}
      >
        {!!imageUrl && (
          <div
            className={classNames(
              'w-1/3',
              'flex',
              'flex-col',
              'justify-center',
            )}
          >
            <div
              className={classNames('relative', 'overflow-hidden', {
                'rounded-sm':
                  style.linkCardShape !== LinkCardShape.RoundedLarge,
                'rounded-[1.125rem]':
                  style.linkCardShape === LinkCardShape.RoundedLarge,
              })}
            >
              <Image
                className={classNames(
                  'mx-auto',
                  'object-cover',
                  'aspect-[121/104]',
                )}
                width={300}
                height={300}
                src={imageUrl}
                alt={title}
              />
              <p
                className={classNames(
                  'absolute',
                  'bottom-0',
                  'text-xs',
                  'px-2',
                  'py-1',
                  'bg-gray-200',
                  'text-black',
                  'rounded-tr-md',
                )}
              >
                Affiliate
              </p>
            </div>
          </div>
        )}
        <div
          className={classNames(
            'flex-1',
            'flex',
            'flex-col',
            'justify-center',
            'text-sm',
          )}
        >
          <p className={classNames('font-bold', 'sm:text-lg', 'mb-3')}>
            {title}
          </p>
          {!!amazonPrice && <p className="text-lg">{amazonPrice}</p>}
        </div>
      </div>
      <AmazonAffiliateLinkModal
        show={isOpen}
        closeModal={closeModal}
        imageUrl={media?.url}
        title={title}
        description={description}
        url={url}
        amazonPrice={amazonPrice}
      />
    </>
  );
};
